import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useFeatureFlagEnabled } from "@posthog/react";
import { useEffect } from "react";
import { FileText, Plus } from "lucide-react";
import { useSession } from "@/hooks/use-session";
import { useSignupForms, useDeleteSignupForm } from "@/hooks/use-signup-forms";
import { trackSignupFormDeleted } from "@/lib/analytics-events";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/_app/_/forms/")({
	component: FormsListPage,
});

function statusVariant(status: string) {
	switch (status) {
		case "active":
			return "default" as const;
		case "paused":
			return "outline" as const;
		default:
			return "secondary" as const;
	}
}

function FormsListPage() {
	const session = useSession();
	const navigate = useNavigate();
	const campaignId = session.campaign?.id;
	const flagEnabled = useFeatureFlagEnabled("signup-forms");

	useEffect(() => {
		if (flagEnabled === false) {
			navigate({ to: "/", search: {} as never });
		}
	}, [flagEnabled, navigate]);

	const { data, isLoading, error } = useSignupForms(campaignId);
	const deleteForm = useDeleteSignupForm(campaignId ?? 0);

	if (flagEnabled !== true) return null;

	if (!campaignId) {
		return (
			<div className="p-6 text-muted-foreground">
				No campaign selected.
			</div>
		);
	}

	return (
		<div className="mx-auto max-w-5xl space-y-6 p-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold tracking-tight">
						Signup Forms
					</h1>
					<p className="text-muted-foreground">
						Create embeddable forms to capture contacts on your
						website
					</p>
				</div>
				<Button asChild>
					<Link to="/forms/new">
						<Plus className="mr-2 h-4 w-4" />
						Create Form
					</Link>
				</Button>
			</div>

			{isLoading ? (
				<div className="space-y-3">
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-10 w-full" />
				</div>
			) : error ? (
				<div className="rounded-lg border border-destructive/50 p-6 text-center text-sm text-destructive">
					Failed to load forms. Please try again.
				</div>
			) : !data?.forms?.length ? (
				<div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12 text-center">
					<FileText className="mb-4 h-10 w-10 text-muted-foreground/50" />
					<h3 className="text-lg font-semibold">No forms yet</h3>
					<p className="mt-1 text-sm text-muted-foreground">
						Create your first signup form to start collecting
						contacts.
					</p>
					<Button asChild className="mt-4">
						<Link to="/forms/new">
							<Plus className="mr-2 h-4 w-4" />
							Create Form
						</Link>
					</Button>
				</div>
			) : (
				<div className="rounded-lg border">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Title</TableHead>
								<TableHead>Slug</TableHead>
								<TableHead>Status</TableHead>
								<TableHead className="text-right">
									Submissions
								</TableHead>
								<TableHead>Created</TableHead>
								<TableHead className="text-right">
									Actions
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{data.forms.map((form) => (
								<TableRow key={form.id}>
									<TableCell className="font-medium">
										<Link
											to="/forms/$formId"
											params={{
												formId: String(form.id),
											}}
											className="hover:underline"
										>
											{form.title}
										</Link>
									</TableCell>
									<TableCell className="font-mono text-xs text-muted-foreground">
										{form.slug}
									</TableCell>
									<TableCell>
										<Badge
											variant={statusVariant(form.status)}
										>
											{form.status}
										</Badge>
									</TableCell>
									<TableCell className="text-right tabular-nums">
										{form.submission_count}
									</TableCell>
									<TableCell className="text-muted-foreground">
										{new Date(
											form.created_at,
										).toLocaleDateString()}
									</TableCell>
									<TableCell className="text-right">
										<div className="flex items-center justify-end gap-2">
											<Button
												variant="ghost"
												size="sm"
												asChild
											>
												<Link
													to="/forms/$formId"
													params={{
														formId: String(
															form.id,
														),
													}}
												>
													Edit
												</Link>
											</Button>
											<Button
												variant="ghost"
												size="sm"
												className="text-destructive hover:text-destructive"
												onClick={() => {
													if (
														window.confirm(
															`Delete "${form.title}"? This cannot be undone.`,
														)
													) {
														deleteForm.mutate(
															form.id,
															{
																onSuccess:
																	() => {
																		trackSignupFormDeleted(
																			form.slug,
																		);
																	},
															},
														);
													}
												}}
												disabled={
													deleteForm.isPending
												}
											>
												Delete
											</Button>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	);
}
